import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatBRL, formatDate } from '@/pages/painel/lib/comercial';

interface Preco {
  id: number;
  fornecedor_id: number;
  produto: string;
  marca: string | null;
  preco: number | null;
  data: string;
  condicao: string | null;
  desconto: number | null;
  frete: string | null;
}

interface FornecedorPrecosProps {
  fornecedorId: number;
  fornecedorNome: string;
  password: string;
  onClose: () => void;
}

export default function FornecedorPrecos({ fornecedorId, fornecedorNome, password, onClose }: FornecedorPrecosProps) {
  const [precos, setPrecos] = useState<Preco[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [produto, setProduto] = useState('');
  const [marca, setMarca] = useState('');
  const [preco, setPreco] = useState('');
  const [condicao, setCondicao] = useState('');
  const [desconto, setDesconto] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: { action: 'fornecedor_precos_listar', password, fornecedor_id: fornecedorId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (data?.error) throw new Error(data.error);
      setPrecos(data?.precos ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar preços.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fornecedorId]);

  const salvar = async () => {
    if (!produto.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('filas-admin', {
        body: {
          action: 'fornecedor_preco_salvar',
          password,
          fornecedor_id: fornecedorId,
          produto: produto.trim(),
          marca: marca.trim() || null,
          preco: preco !== '' ? Number(preco) : null,
          condicao: condicao.trim() || null,
          desconto: desconto !== '' ? Number(desconto) : null,
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      if (data?.error) throw new Error(data.error);
      setProduto('');
      setMarca('');
      setPreco('');
      setCondicao('');
      setDesconto('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar preço.');
    } finally {
      setBusy(false);
    }
  };

  const excluir = async (id: number) => {
    await supabase.functions.invoke('filas-admin', {
      body: { action: 'fornecedor_preco_excluir', password, id },
    });
    await load();
  };

  const inputCls = 'border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A] bg-white';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Histórico de preços</h3>
            <p className="text-xs text-gray-500">{fornecedorNome}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-600 mb-3">Registrar novo preço</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <input value={produto} onChange={(e) => setProduto(e.target.value)} placeholder="Produto *" className={inputCls} />
              <input value={marca} onChange={(e) => setMarca(e.target.value)} placeholder="Marca" className={inputCls} />
              <input type="number" value={preco} onChange={(e) => setPreco(e.target.value)} placeholder="Preço" className={inputCls} />
              <input value={condicao} onChange={(e) => setCondicao(e.target.value)} placeholder="Condição" className={inputCls} />
              <input type="number" value={desconto} onChange={(e) => setDesconto(e.target.value)} placeholder="Desconto %" className={inputCls} />
              <button onClick={salvar} disabled={busy || !produto.trim()} className="inline-flex items-center justify-center gap-1 bg-[#E8610A] text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-50">
                {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                Adicionar
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          {loading ? (
            <div className="flex justify-center py-8"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
          ) : precos.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">Nenhum preço registrado ainda.</p>
          ) : (
            <div className="space-y-2">
              {precos.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{p.produto}{p.marca ? ` · ${p.marca}` : ''}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(p.data)}{p.condicao ? ` · ${p.condicao}` : ''}{p.desconto != null ? ` · ${p.desconto}% desc.` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-bold text-gray-900">{formatBRL(p.preco)}</span>
                    <button onClick={() => excluir(p.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 cursor-pointer">
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}